import React, { useState, useEffect, useRef } from 'react';
import { Paper, Stack, Text, Button, Group, CloseButton, Box } from '@mantine/core';
import { IconSquareRoundedPlus, IconDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { DISMISS_KEY, isInStandaloneMode, getPWAInstallScenario } from './pwaUtils';

// iOS Share Icon Component (Apple-style share icon)
// SVG from: https://www.svgrepo.com/svg/349629/share-apple
const IOSShareIcon: React.FC<{ size?: number; color?: string }> = ({ size = 21, color = 'var(--mantine-color-blue-6)' }) => {
    return (
        <svg
            width={size}
            height={size}
            viewBox="3 3 44 44"
            fill={color}
            stroke={color}
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
        >
            <path d="M30.3 13.7L25 8.4l-5.3 5.3-1.4-1.4L25 5.6l6.7 6.7z"/>
            <path d="M24 7h2v21h-2z"/>
            <path d="M35 40H15c-1.7 0-3-1.3-3-3V19c0-1.7 1.3-3 3-3h7v2h-7c-.6 0-1 .4-1 1v18c0 .6.4 1 1 1h20c.6 0 1-.4 1-1V19c0-.6-.4-1-1-1h-7v-2h7c1.7 0 3 1.3 3 3v18c0 1.7-1.3 3-3 3z"/>
        </svg>
    );
};

// Extend Window interface for beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
    interface WindowEventMap {
        beforeinstallprompt: BeforeInstallPromptEvent;
    }
}

type PromptType = 'hidden' | 'native-install' | 'ios-safari' | 'ios-non-safari' | 'macos-safari' | 'android-manual';

export const PWAInstallPrompt: React.FC = () => {
    const { i18n } = useTranslation();
    const [promptType, setPromptType] = useState<PromptType>('hidden');
    const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        if (isInStandaloneMode()) return;

        const dismissed = localStorage.getItem(DISMISS_KEY);
        const scenario = getPWAInstallScenario();

        // Always register beforeinstallprompt listener (fires for native-prompt scenario)
        const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
            // Prevent Chrome's mini-infobar
            e.preventDefault();
            // Store the event for later use
            deferredPromptRef.current = e;
            // Show our install button if not dismissed
            if (!dismissed)
                setPromptType('native-install');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Handle successful installation
        const handleAppInstalled = () => {
            setPromptType('hidden');
            deferredPromptRef.current = null;
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        // For non-native scenarios, show after 2s delay if not dismissed
        let timer: ReturnType<typeof setTimeout> | undefined;
        if (!dismissed && scenario !== 'native-prompt' && scenario !== 'unsupported') {
            timer = setTimeout(() => {
                setPromptType(scenario as Exclude<PromptType, 'hidden' | 'native-install'>);
            }, 2000);
        }

        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPromptRef.current)
            return;

        // Show the install prompt
        await deferredPromptRef.current.prompt();

        // Wait for user response
        const { outcome } = await deferredPromptRef.current.userChoice;

        // Clear the deferred prompt
        deferredPromptRef.current = null;
        setPromptType('hidden');

        if (outcome === 'dismissed')
            localStorage.setItem(DISMISS_KEY, 'true');
    };

    const handleDismiss = () => {
        localStorage.setItem(DISMISS_KEY, 'true');
        setPromptType('hidden');
    };

    const handleOpenInSafari = () => {
        const currentUrl = window.location.href;
        const isFrench = i18n.language?.startsWith('fr');

        // Try to open Safari directly using URL scheme
        let safariUrl = currentUrl;
        if (currentUrl.startsWith('https://'))
            safariUrl = currentUrl.replace('https://', 'x-safari-https://');
         else if (currentUrl.startsWith('http://'))
            safariUrl = currentUrl.replace('http://', 'x-safari-http://');

        // Set a flag to track if page loses focus (indicating successful redirect)
        let didRedirect = false;

        // If the page loses focus (blur), it means Safari is opening
        const handleBlur = () => {
            didRedirect = true;
            setTimeout(() => {
                handleDismiss();
            }, 500);
        };

        window.addEventListener('blur', handleBlur, { once: true });

        try {
            // Attempt to open Safari
            window.location.href = safariUrl;

            // After a short delay, check if redirect happened
            setTimeout(() => {
                window.removeEventListener('blur', handleBlur);

                if (!didRedirect) {
                    // URL scheme didn't work, fall back to clipboard
                    copyUrlAndShowInstructions(currentUrl, isFrench);
                }
            }, 1500);
        } catch (error) {
            console.error('Failed to open Safari:', error);
            window.removeEventListener('blur', handleBlur);
            // Fall back to clipboard method
            copyUrlAndShowInstructions(currentUrl, isFrench);
        }
    };

    const copyUrlAndShowInstructions = (url: string, isFrench: boolean) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                alert(isFrench
                    ? 'URL copiée! Ouvrez Safari et collez l\'URL dans la barre d\'adresse.'
                    : 'URL copied! Open Safari and paste the URL in the address bar.');
            }).catch(() => {
                alert(isFrench
                    ? 'Veuillez ouvrir Safari et accéder à: ' + url
                    : 'Please open Safari and go to: ' + url);
            });
        } else {
            alert(isFrench
                ? 'Veuillez ouvrir Safari et accéder à: ' + url
                : 'Please open Safari and go to: ' + url);
        }
    };

    if (promptType === 'hidden') return null;

    const isFrench = i18n.language?.startsWith('fr');

    switch (promptType) {
        case 'native-install':
            return (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: '16px',
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
                    }}
                >
                    <Paper
                        shadow="xl"
                        radius="lg"
                        p="md"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid var(--mantine-color-gray-3)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                                <Text fw={600} size="md" c="dark">
                                    {isFrench ? 'Installer AppMed' : 'Install AppMed'}
                                </Text>
                                <CloseButton onClick={handleDismiss} aria-label="Dismiss" />
                            </Group>

                            <Text size="sm" c="dimmed">
                                {isFrench
                                    ? 'Installez AppMed sur votre appareil pour un accès rapide et une meilleure expérience.'
                                    : 'Install AppMed on your device for quick access and a better experience.'}
                            </Text>

                            <Group gap="sm" grow>
                                <Button
                                    variant="filled"
                                    color="blue"
                                    size="sm"
                                    leftSection={<IconDownload size={18} />}
                                    onClick={handleInstallClick}
                                >
                                    {isFrench ? 'Installer' : 'Install'}
                                </Button>
                                <Button
                                    variant="light"
                                    color="gray"
                                    size="sm"
                                    onClick={handleDismiss}
                                >
                                    {isFrench ? 'Plus tard' : 'Later'}
                                </Button>
                            </Group>
                        </Stack>
                    </Paper>
                </Box>
            );

        case 'ios-non-safari':
            return (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: '16px',
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
                    }}
                >
                    <Paper
                        shadow="xl"
                        radius="lg"
                        p="md"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid var(--mantine-color-gray-3)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                                <Text fw={600} size="md" c="dark">
                                    {isFrench ? 'Installer AppMed' : 'Install AppMed'}
                                </Text>
                                <CloseButton onClick={handleDismiss} aria-label="Dismiss" />
                            </Group>

                            <Text size="sm" c="dimmed">
                                {isFrench
                                    ? 'Pour installer AppMed sur votre appareil iOS, vous devez utiliser Safari.'
                                    : 'To install AppMed on your iOS device, you need to use Safari.'}
                            </Text>

                            <Group gap="sm" grow>
                                <Button
                                    variant="filled"
                                    color="blue"
                                    size="sm"
                                    onClick={handleOpenInSafari}
                                >
                                    {isFrench ? 'Ouvrir dans Safari' : 'Open in Safari'}
                                </Button>
                                <Button
                                    variant="light"
                                    color="gray"
                                    size="sm"
                                    onClick={handleDismiss}
                                >
                                    {isFrench ? 'Plus tard' : 'Later'}
                                </Button>
                            </Group>
                        </Stack>
                    </Paper>
                </Box>
            );

        case 'android-manual':
            return (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: '16px',
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
                    }}
                >
                    <Paper
                        shadow="xl"
                        radius="lg"
                        p="md"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid var(--mantine-color-gray-3)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                                <Text fw={600} size="md" c="dark">
                                    {isFrench ? 'Installer AppMed' : 'Install AppMed'}
                                </Text>
                                <CloseButton onClick={handleDismiss} aria-label="Dismiss" />
                            </Group>

                            <Text size="sm" c="dimmed">
                                {isFrench
                                    ? 'Ajoutez AppMed à votre écran d\'accueil pour un accès rapide.'
                                    : 'Add AppMed to your home screen for quick access.'}
                            </Text>

                            <Stack gap="xs">
                                <Text size="sm" fw={500}>
                                    {isFrench
                                        ? '1. Appuyez sur le menu du navigateur (⋮)'
                                        : '1. Tap the browser menu (⋮)'}
                                </Text>
                                <Text size="sm" fw={500}>
                                    {isFrench
                                        ? '2. Sélectionnez "Ajouter à l\'écran d\'accueil"'
                                        : '2. Select "Add to Home screen"'}
                                </Text>
                            </Stack>

                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                fullWidth
                                onClick={handleDismiss}
                                mt="xs"
                            >
                                {isFrench ? 'Compris' : 'Got it'}
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            );

        case 'ios-safari':
            return (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        padding: '16px',
                        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
                    }}
                >
                    <Paper
                        shadow="xl"
                        radius="lg"
                        p="md"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid var(--mantine-color-gray-3)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                                <Text fw={600} size="md" c="dark">
                                    {isFrench ? 'Installer AppMed' : 'Install AppMed'}
                                </Text>
                                <CloseButton onClick={handleDismiss} aria-label="Dismiss" />
                            </Group>

                            <Text size="sm" c="dimmed">
                                {isFrench
                                    ? 'Ajoutez AppMed à votre écran d\'accueil pour un accès rapide.'
                                    : 'Add AppMed to your home screen for quick access.'}
                            </Text>

                            <Stack gap="xs">
                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            backgroundColor: 'var(--mantine-color-blue-0)',
                                            borderRadius: '8px',
                                            padding: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <IOSShareIcon size={18} color="var(--mantine-color-blue-6)" />
                                    </Box>
                                    <Text size="sm">
                                        {isFrench
                                            ? '1. Appuyez sur le bouton Partager'
                                            : '1. Tap the Share button'}
                                    </Text>
                                </Group>

                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            backgroundColor: 'var(--mantine-color-blue-0)',
                                            borderRadius: '8px',
                                            padding: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <IconSquareRoundedPlus size={18} color="var(--mantine-color-blue-6)" />
                                    </Box>
                                    <Text size="sm">
                                        {isFrench
                                            ? '2. Sélectionnez "Sur l\'écran d\'accueil"'
                                            : '2. Select "Add to Home Screen"'}
                                    </Text>
                                </Group>
                            </Stack>

                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                fullWidth
                                onClick={handleDismiss}
                                mt="xs"
                            >
                                {isFrench ? 'Compris' : 'Got it'}
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            );

        case 'macos-safari':
            return (
                <Box
                    style={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        zIndex: 1000,
                        maxWidth: '360px',
                    }}
                >
                    <Paper
                        shadow="xl"
                        radius="lg"
                        p="md"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid var(--mantine-color-gray-3)',
                        }}
                    >
                        <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                                <Text fw={600} size="md" c="dark">
                                    {isFrench ? 'Installer AppMed' : 'Install AppMed'}
                                </Text>
                                <CloseButton onClick={handleDismiss} aria-label="Dismiss" />
                            </Group>

                            <Text size="sm" c="dimmed">
                                {isFrench
                                    ? 'Ajoutez AppMed au Dock pour un accès rapide.'
                                    : 'Add AppMed to your Dock for quick access.'}
                            </Text>

                            <Stack gap="xs">
                                <Group gap="xs" align="center">
                                    <Box
                                        style={{
                                            backgroundColor: 'var(--mantine-color-blue-0)',
                                            borderRadius: '8px',
                                            padding: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <IOSShareIcon size={18} color="var(--mantine-color-blue-6)" />
                                    </Box>
                                    <Text size="sm">
                                        {isFrench
                                            ? '1. Menu Fichier → Ajouter au Dock'
                                            : '1. File menu → Add to Dock'}
                                    </Text>
                                </Group>

                                <Text size="xs" c="dimmed" pl={42}>
                                    {isFrench
                                        ? 'ou utilisez le bouton Partager'
                                        : 'or use the Share button'}
                                </Text>
                            </Stack>

                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                fullWidth
                                onClick={handleDismiss}
                                mt="xs"
                            >
                                {isFrench ? 'Compris' : 'Got it'}
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            );

        default:
            return null;
    }
};

export default PWAInstallPrompt;
